"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Clock, Utensils, Music, MapPin, Phone } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useRef, useState } from "react";
const events = [{
  id: 1,
  icon: Clock,
  title: "Cocktail Hour",
  description: "Join us for refreshing drinks and light bites as we gather to celebrate the start of an unforgettable evening under the Lebanese mountain sky."
}, {
  id: 2,
  icon: Utensils,
  title: "Dinner",
  description: "Savor an exquisite feast crafted with the finest local ingredients, served family-style amidst the elegance of Le Clos de Faqra."
}, {
  id: 3,
  icon: Music,
  title: "Dancing",
  description: "Let the music move you as we dance the night away surrounded by ancient ruins, twinkling lights, and the joy of love and laughter."
}];
const scheduleItems = [{
  id: 1,
  time: "5:45 PM",
  eventName: "Ceremony Begins",
  note: "Guests arrive and take their seats under the open sky"
}, {
  id: 2,
  time: "6:45 PM",
  eventName: "Cocktail Hour",
  note: "Refreshments and mingling in the garden courtyard"
}, {
  id: 3,
  time: "8:00 PM",
  eventName: "Reception Dinner",
  note: "A buffet inspired by international cuisine"
}];
export default function Rsvp() {
  const {
    ref: receptionRef,
    isVisible: receptionVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: ref1,
    isVisible: isVisible1
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: ref2,
    isVisible: isVisible2
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: ref3,
    isVisible: isVisible3
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: ceremonyRef,
    isVisible: ceremonyVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: scheduleRef,
    isVisible: scheduleVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  const {
    ref: rsvpRef,
    isVisible: rsvpVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const refs = [ref1, ref2, ref3];
  const visibles = [isVisible1, isVisible2, isVisible3];
  const mapRef = useRef<HTMLDivElement>(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (mapRef.current) {
        const rect = mapRef.current.getBoundingClientRect();
        const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        setParallaxOffset(scrollProgress * 20);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <>
      <section className="bg-[hsl(135,15%,95%)] py-16 px-4 sm:px-6 lg:px-8 !bg-[url(https://vanessabrian.com/wp-content/uploads/2026/02/background-3.jpg)]">
        <div ref={ceremonyRef} className="mx-auto max-w-7xl" style={{
        opacity: ceremonyVisible ? 1 : 0,
        transform: ceremonyVisible ? "translateY(0)" : "translateY(10px)",
        transition: "opacity 150ms cubic-bezier(0.4,0,0.2,1), transform 150ms cubic-bezier(0.4,0,0.2,1)"
      }}>
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
            <div className="flex flex-col justify-center">
              <Card className="bg-background/80 backdrop-blur-sm border-muted shadow-soft">
                <CardContent className="space-y-6 p-8">
                  <div>
                    <h2 className="font-display text-3xl font-semibold text-foreground mb-2">
                      Ceremony Details
                    </h2>
                    <div className="h-1 w-16 bg-primary rounded-full"></div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-1 h-5 w-5 flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                        <div className="h-2 w-2 rounded-full bg-primary"></div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                          Date
                        </p>
                        <p className="text-lg font-semibold text-foreground">July 11, 2026</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="mt-1 h-5 w-5 flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                        <div className="h-2 w-2 rounded-full bg-primary"></div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                          Time
                        </p>
                        <p className="text-lg font-semibold text-foreground">5:30 PM</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <MapPin className="mt-1 h-5 w-5 flex-shrink-0 text-primary-dark" />
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                          Venue
                        </p>
                        <p className="text-lg font-semibold text-foreground">Le Clos de Faqra</p>
                        <p className="text-base text-muted-foreground mt-1">Kfardebian, Lebanon</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="relative w-full rounded-lg overflow-hidden shadow-soft border border-muted">
                      <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3307.882557701974!2d35.804441!3d33.997989!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151f3f9c8c8c8c8d%3A0x1c8c8c8c8c8c8c8c!2sLe%20Clos%20de%20Faqra!5e0!3m2!1sen!2slb!4v1234567890" width="100%" height="280" style={{
                      border: 0
                    }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Le Clos de Faqra Location Map" className="w-full"></iframe>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col gap-6">
              <div ref={mapRef} className="relative overflow-hidden rounded-lg shadow-medium" style={{
              transform: `translateY(${parallaxOffset}px)`,
              transition: "transform 0.1s ease-out"
            }}>
                <img src="https://sweeping-roughy.10web.cloud/wp-content/uploads/2026/01/ChatGPT-Image-Jan-10-2026-09_35_00-PM-1.png" alt="Map to Le Clos de Faqra" className="w-full object-contain !bg-[hsl(60,9%,98%)]" />
                <div className="absolute inset-0 bg-primary/10"></div>
              </div>

              <Card className="bg-background/90 backdrop-blur-sm border-muted shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 flex-shrink-0 rounded-full bg-secondary/10 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-secondary-dark" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                        Parking & Directions
                      </h3>
                      <p className="text-base text-muted-foreground leading-relaxed">Complementary Valet Services will be provided at the Venue Entrance. Located midway between Les Dunes de Fakra and the Faqra/ Faraya Roundabout.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-[hsl(45,15%,96%)] py-20 px-4 !bg-[url(https://vanessabrian.com/wp-content/uploads/2026/02/background-3.jpg)]">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-center text-4xl font-semibold text-[hsl(100,35%,30%)] mb-16">
            Schedule
          </h2>

          <div ref={scheduleRef} className={`
              space-y-0
              transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)]
              ${scheduleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}
            `}>
            {scheduleItems.map((item, i) => <div key={item.id} data-index={i} className="
                  group relative grid grid-cols-[1fr_auto_1fr] items-start gap-6 py-8
                  border-b border-[hsl(45,20%,85%)] last:border-b-0
                  transition-all duration-[120ms] ease-[var(--ease-smooth)]
                  hover:scale-[1.03] hover:bg-[hsl(45,15%,98%)]
                " style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cpath d='M30 5 Q50 15 30 30 Q10 15 30 5 Z' fill='%23e8e4dc' fill-opacity='0.3'/%3E%3C/svg%3E")`,
            backgroundSize: "120px 120px",
            backgroundPosition: "center center",
            backgroundRepeat: "repeat"
          }}>
                <div className="text-right pr-4">
                  <span className="font-sans text-lg font-semibold text-[hsl(135,45%,45%)]">
                    {item.time}
                  </span>
                </div>

                <div className="w-px bg-[hsl(45,20%,85%)] self-stretch" />

                <div className="pl-4">
                  <h3 className="font-display text-xl font-semibold text-[hsl(100,35%,30%)] mb-1">
                    {item.eventName}
                  </h3>
                  <p className="font-sans text-sm text-[hsl(100,35%,40%)] leading-relaxed">
                    {item.note}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      <section ref={rsvpRef} className="py-20 px-4 bg-light-background !bg-[url(https://vanessabrian.com/wp-content/uploads/2026/02/background-3.jpg)]" style={{
      opacity: rsvpVisible ? 1 : 0,
      transform: rsvpVisible ? "translateY(0)" : "translateY(10px)",
      transition: "opacity 150ms cubic-bezier(0.4,0,0.2,1), transform 150ms cubic-bezier(0.4,0,0.2,1)"
    }} id="rsvp">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-display text-primary-dark">RSVP here:</h2>
              <p className="text-lg text-foreground leading-relaxed">
                We would be honored to celebrate with you. Please let us know if you can attend by
                filling out the form. Your presence would mean the world to us as we begin this new
                chapter together.
                <div className="!font-semibold">
                  Please submit a separate form for each guest who will be attending.
                </div>
              </p>
              <p className="text-sm text-foreground !font-bold">
                Kindly respond by May 1, 2026&nbsp;
              </p>
            </div>

            <FormProvider className="bg-accent/50 rounded-lg shadow-soft p-8 border border-border" formId="rsvp-form-hero" sectionName="rsvp-section" defaultValues={{
            fullName: "",
            fullName2: "",
            fullName3: "",
            fullName4: "",
            fullName5: "",
            fullName6: "",
            email: "",
            phone: "",
            message: "",
            attendance: ""
          }} validationRules={{
            fullName: {
              required: true,
              minLength: 2,
              maxLength: 100
            },
            fullName2: {
              required: false,
              minLength: 2,
              maxLength: 100
            },
            fullName3: {
              required: false,
              minLength: 2,
              maxLength: 100
            },
            fullName4: {
              required: false,
              minLength: 2,
              maxLength: 100
            },
            fullName5: {
              required: false,
              minLength: 2,
              maxLength: 100
            },
            fullName6: {
              required: false,
              minLength: 2,
              maxLength: 100
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 500
            },
            attendance: {
              required: true
            }
          }} submitText="Submit RSVP" successMessage="Thank you for your RSVP! We look forward to celebrating with you." emailRecipients={["brianabinader@gmail.com", "vanessa_dwairi@hotmail.com"]}>
              <RsvpForm />
            </FormProvider>
          </div>
        </div>
      </section>
    </>;
}
function RsvpForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary-light border border-primary/20 rounded-lg">
        <p className="text-foreground text-center">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 w-full bg-background hover:bg-accent border-border">
          Submit Another Response
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your full name" {...field} className="bg-background border-border text-foreground focus:scale-[1.03] focus:bg-accent-light transition-all duration-[120ms] ease-[var(--ease-smooth)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="fullName2" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans">Additional Guest Name (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Guest name (if applicable)" {...field} className="bg-background border-border text-foreground focus:scale-[1.03] focus:bg-accent-light transition-all duration-[120ms] ease-[var(--ease-smooth)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="fullName3" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans">Additional Guest Name (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Guest name (if applicable)" {...field} className="bg-background border-border text-foreground focus:scale-[1.03] focus:bg-accent-light transition-all duration-[120ms] ease-[var(--ease-smooth)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="fullName4" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans">Additional Guest Name (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Guest name (if applicable)" {...field} className="bg-background border-border text-foreground focus:scale-[1.03] focus:bg-accent-light transition-all duration-[120ms] ease-[var(--ease-smooth)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="fullName5" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans">Additional Guest Name (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Guest name (if applicable)" {...field} className="bg-background border-border text-foreground focus:scale-[1.03] focus:bg-accent-light transition-all duration-[120ms] ease-[var(--ease-smooth)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="fullName6" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans">Additional Guest Name (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Guest name (if applicable)" {...field} className="bg-background border-border text-foreground focus:scale-[1.03] focus:bg-accent-light transition-all duration-[120ms] ease-[var(--ease-smooth)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} className="bg-background border-border text-foreground focus:scale-[1.03] focus:bg-accent-light transition-all duration-[120ms] ease-[var(--ease-smooth)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="bg-background border-border text-foreground focus:scale-[1.03] focus:bg-accent-light transition-all duration-[120ms] ease-[var(--ease-smooth)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="attendance" rules={{
        required: "Please select your attendance status"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans">Will you attend?</FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-xs transition-all focus:scale-[1.03] focus:bg-accent-light focus:outline-none focus:ring-2 focus:ring-ring duration-[120ms] ease-[var(--ease-smooth)]">
                  <option value="">Select an option</option>
                  <option value="yes">Joyfully accepts</option>
                  <option value="no">Regretfully declines</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        },
        maxLength: {
          value: 500,
          message: "Message must not exceed 500 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans">
                Message to Vanessa and Brian
              </FormLabel>
              <FormControl>
                <textarea placeholder="Share your thoughts, wishes, or a special message..." rows={4} {...field} className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-xs transition-all focus:scale-[1.03] focus:bg-accent-light focus:outline-none focus:ring-2 focus:ring-ring duration-[120ms] ease-[var(--ease-smooth)] placeholder:text-muted-foreground resize-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary text-white hover:bg-secondary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-secondary-active active:translate-y-0 transition-all duration-[120ms] ease-[var(--ease-spring)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting...
            </> : "Submit RSVP"}
        </Button>
      </div>
    </Form>;
}